import type { AxiosResponse } from 'axios';
import React, { createContext, useContext } from 'react';
import {
	extractOfDataQuery,
	useAxiosInstance,
	usePoMutation,
} from '@gov-nx/api';
import { useExtraConfigContext } from '@gov-nx/core/app';
import { useMessageEvents } from '@gov-nx/core/events';
import { useProcessControl } from '@gov-nx/core/hooks';
import {
	getRequiredDataBoxes,
	useTranslationWithNamespace,
} from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import { compose } from '@gov-nx/core/types';
import type { ServiceContextProviderProps } from '../vypis-z-zivnostenskeho-rejstriku/Context';
import { FormInstance, prepareSubmitData } from './FormDefinitions';
import type { FormData, ServiceContextTypes } from './service.types';

export const ServiceContext =
	createContext<Nullable<ServiceContextTypes>>(null);

export function VypisVyuzitiUdajuZROSContextProvider({
	children,
	code,
}: ServiceContextProviderProps) {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tsn = getLocalizeCurried(code);
	const {
		extraConfig: { application },
	} = useExtraConfigContext();
	const requiredDataBoxes = getRequiredDataBoxes(code, application);
	const formDefinition = FormInstance({
		code,
		dataBoxTypes: requiredDataBoxes,
	});
	const { toastMessageSuccess, toastMessageError } = useMessageEvents();
	const { controls, setControls } = useProcessControl();
	const { axiosInstance } = useAxiosInstance();

	const submitMutation = usePoMutation<AxiosResponse<void>, FormData>({
		mutationFn: compose(extractOfDataQuery(axiosInstance), prepareSubmitData),
		onSuccess: () => {
			toastMessageSuccess(tsn('form.messages.odeslano'));
			formDefinition.formReset();
		},
		onError: () => {
			toastMessageError(tsn('form.messages.chyba-pri-odesilani'), {
				time: 0,
			});
		},
	});

	const onSubmit = formDefinition.formMethods.handleSubmit((data) =>
		submitMutation.mutate(data)
	);

	return (
		<ServiceContext.Provider
			value={{
				code,
				controls,
				setControls,
				formDefinition,
				requiredDataBoxes,
				onSubmit,
				submitMutation,
			}}>
			{children}
		</ServiceContext.Provider>
	);
}

export const useVypisVyuzitiUdajuZROSContext = (): ServiceContextTypes =>
	useContext(ServiceContext) as ServiceContextTypes;
